import { TYPE } from "@gaoya/shared";
import { C, capsuleBox, clipName, drawPanel, drawSeal, fillRound, title } from "../theme.js";
import type { UiState } from "../../game/state.js";

export type ResultBand = { x: number; y: number; w: number; h: number };

export type ResultLayout = {
  stats: ResultBand;
  chain: ResultBand;
};

export function resultLayout(w: number, h: number, ui: UiState): ResultLayout {
  const btnY = Math.min(h * 0.74, h - ui.safeBottom - 160);
  const cap = capsuleBox(ui, w);
  const compact = h < 720;
  const heroTop = cap.bottom + (compact ? 6 : 14);
  const statsY = heroTop + (compact ? 136 : 164);
  const stats: ResultBand = { x: 16, y: statsY, w: w - 32, h: 104 };
  if (ui.mode === "RUSH" && ui.gameOver?.ranking) {
    stats.h = Math.min(196, 20 + ui.gameOver.ranking.length * 42);
  }
  const chainY = stats.y + stats.h + 12;
  const chainH = Math.max(80, btnY - 12 - chainY);
  return {
    stats,
    chain: { x: 16, y: chainY, w: w - 32, h: chainH },
  };
}

export function resultHeadline(ui: UiState): string {
  const go = ui.gameOver;
  if (ui.mode === "RUSH") {
    const row = go?.ranking?.find((r) => r.userId === ui.userId);
    if (!row) return "茶桌收局";
    if (row.rank === 1) return go?.ranking?.filter((r) => r.rank === 1).length === 1 ? "状元 · 你赢了" : "并列第一";
    const titles = ["", "状元", "榜眼", "探花", "同案"];
    return `第 ${row.rank} 名 · ${titles[row.rank] ?? "同案"}`;
  }
  if (!go || go.winnerId === null) return "旗鼓相当";
  return go.winnerId === ui.userId ? "对方茶尽 · 你赢了" : "此巡茶尽 · 再战一局";
}

export function reasonText(r: string): string {
  switch (r) {
    case "HP_ZERO":
      return "茶尽见分晓";
    case "SURRENDER":
      return "有人拱手认输";
    case "DISCONNECT":
      return "对方久未归座";
    case "MAX_ROUNDS":
      return "回合用尽，按余力定胜负";
    case "TIME_UP":
      return "四分钟到点，按分排名";
    default:
      return "";
  }
}

function outcome(ui: UiState): "win" | "lose" | "draw" {
  const go = ui.gameOver;
  if (ui.mode === "RUSH") {
    const row = go?.ranking?.find((r) => r.userId === ui.userId);
    if (!row) return "draw";
    return row.rank === 1 ? "win" : "lose";
  }
  if (!go || go.winnerId === null) return "draw";
  return go.winnerId === ui.userId ? "win" : "lose";
}

export function drawResult(ctx: CanvasRenderingContext2D, ui: UiState, w: number, h: number): void {
  const go = ui.gameOver;
  const g = resultLayout(w, h, ui);
  const cap = capsuleBox(ui, w);
  const compact = h < 720;
  const heroTop = cap.bottom + (compact ? 6 : 14);
  const o = outcome(ui);
  const sealSize = compact ? 60 : 72;
  const sealCy = heroTop + sealSize / 2 + 4;
  // 光晕
  ctx.fillStyle = o === "win" ? "rgba(228,185,95,0.22)" : "rgba(158,141,126,0.14)";
  ctx.beginPath();
  ctx.arc(w / 2, sealCy, sealSize * 0.95, 0, Math.PI * 2);
  ctx.fill();
  const rank = ui.mode === "RUSH" ? go?.ranking?.find((r) => r.userId === ui.userId)?.rank : undefined;
  const sealText = rank ? (["", "魁", "榜", "探", "同"][rank] ?? "同") : o === "win" ? "胜" : o === "lose" ? "负" : "和";
  drawSeal(ctx, w / 2, sealCy, sealSize, sealText, o === "win" ? C.seal : o === "lose" ? "#6E5E51" : C.goldDeep, "#FFF7EE", -0.06);
  const ty = heroTop + sealSize + (compact ? 30 : 38);
  title(ctx, resultHeadline(ui), w / 2, ty, compact ? 22 : 26);
  ctx.fillStyle = C.ink2;
  ctx.font = `13px ${TYPE.sans}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const reason = go ? reasonText(go.reason) : "";
  const doubled = o === "win" && (go?.winDelta?.[ui.userId] ?? 0) >= 2;
  ctx.fillText(doubled ? (reason ? `${reason} · 胜场 ×2` : "胜场 ×2") : reason, w / 2, ty + (compact ? 24 : 28));

  drawPanel(ctx, g.stats.x, g.stats.y, g.stats.w, g.stats.h, 16, C.card);
  if (ui.mode === "RUSH" && go?.ranking) {
    const rowH = 42;
    for (let i = 0; i < go.ranking.length; i++) {
      const row = go.ranking[i];
      if (!row) continue;
      const mine = row.userId === ui.userId;
      const ry = g.stats.y + 10 + i * rowH;
      if (ry + rowH > g.stats.y + g.stats.h) break;
      if (mine) fillRound(ctx, g.stats.x + 8, ry + 2, g.stats.w - 16, rowH - 4, 10, "rgba(228,185,95,0.2)");
      else if (i > 0) {
        ctx.fillStyle = C.line;
        ctx.fillRect(g.stats.x + 16, ry, g.stats.w - 32, 1);
      }
      const cy = ry + rowH / 2;
      const medal = MEDAL[row.rank - 1];
      if (medal) {
        fillRound(ctx, g.stats.x + 18, cy - 11, 22, 22, 11, medal[0]);
        ctx.fillStyle = medal[1];
      } else ctx.fillStyle = C.ink3;
      ctx.font = `700 12px ${TYPE.sans}`;
      ctx.textAlign = "center";
      ctx.fillText(String(row.rank), g.stats.x + 29, cy + 0.5);
      ctx.textAlign = "left";
      ctx.fillStyle = C.ink;
      ctx.font = `${mine ? 700 : 500} 15px ${TYPE.sans}`;
      ctx.fillText(clipName(ctx, row.nickname + (mine ? "（我）" : ""), g.stats.w - 140), g.stats.x + 52, cy);
      ctx.textAlign = "right";
      ctx.fillStyle = C.seal;
      ctx.font = `700 17px ${TYPE.serif}`;
      ctx.fillText(String(row.score), g.stats.x + g.stats.w - 36, cy);
      ctx.fillStyle = C.ink2;
      ctx.font = `12px ${TYPE.sans}`;
      ctx.fillText("分", g.stats.x + g.stats.w - 18, cy);
    }
  } else {
    const stats = go?.stats[ui.userId];
    const cells = [
      ["答对", String(stats?.correct ?? 0)],
      ["失误", String(stats?.mistakes ?? 0)],
      ["超时", String(stats?.timeouts ?? 0)],
      ["最高连击", String(stats?.maxCombo ?? 0)],
    ];
    const cw = g.stats.w / 4;
    for (let i = 0; i < cells.length; i++) {
      const c = cells[i];
      if (!c) continue;
      const cx = g.stats.x + cw * i + cw / 2;
      if (i > 0) {
        ctx.fillStyle = C.line;
        ctx.fillRect(g.stats.x + cw * i, g.stats.y + 18, 1, 44);
      }
      ctx.fillStyle = i === 0 ? C.seal : C.ink;
      ctx.font = `700 26px ${TYPE.serif}`;
      ctx.textAlign = "center";
      ctx.fillText(c[1] ?? "0", cx, g.stats.y + 34);
      ctx.fillStyle = C.ink2;
      ctx.font = `12px ${TYPE.sans}`;
      ctx.fillText(c[0] ?? "", cx, g.stats.y + 58);
    }
    fillRound(ctx, g.stats.x + 12, g.stats.y + 74, g.stats.w - 24, 22, 11, C.cardSoft);
    ctx.fillStyle = C.ink2;
    ctx.font = `12px ${TYPE.sans}`;
    ctx.fillText(
      `平均用时 ${Math.round((stats?.avgResponseMs ?? 0) / 100) / 10} 秒 · 共 ${go?.totalRounds ?? 0} 回合`,
      w / 2,
      g.stats.y + 85.5,
    );
  }

  drawChainRecord(ctx, ui, g.chain);
}

const MEDAL: [string, string][] = [
  ["#E4B95F", "#6B4A12"],
  ["#D9D4CB", "#4E4740"],
  ["#E2B48C", "#6A3E1E"],
];

type Chip = { word: string; x: number; y: number; w: number };

function flowChips(ctx: CanvasRenderingContext2D, words: string[], maxW: number, chipH: number, gap: number): { chips: Chip[]; h: number } {
  const chips: Chip[] = [];
  let x = 0;
  let y = 0;
  const arrow = 14;
  for (let i = 0; i < words.length; i++) {
    const word = words[i] ?? "";
    const cw = ctx.measureText(word).width + 20;
    const need = cw + (i < words.length - 1 ? arrow : 0);
    if (x > 0 && x + need > maxW) {
      x = 0;
      y += chipH + gap;
    }
    chips.push({ word, x, y, w: cw });
    x += cw + arrow;
  }
  return { chips, h: words.length ? y + chipH : 0 };
}

function drawChainRecord(ctx: CanvasRenderingContext2D, ui: UiState, card: ResultBand): void {
  drawPanel(ctx, card.x, card.y, card.w, card.h, 16, C.card);
  ctx.fillStyle = C.ink;
  ctx.font = `700 15px ${TYPE.serif}`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText("接龙记录", card.x + 16, card.y + 20);

  const go = ui.gameOver;
  const words = go?.wordChain ?? ui.wordChain;
  ctx.fillStyle = C.ink3;
  ctx.font = `12px ${TYPE.sans}`;
  ctx.fillText(`${words.length} 条`, card.x + 86, card.y + 20.5);
  const innerX = card.x + 14;
  const innerY = card.y + 40;
  const innerW = card.w - 32;
  const innerH = card.h - 50;
  const chipH = 30;
  ctx.font = `15px ${TYPE.serif}`;
  const flow = flowChips(ctx, words, innerW, chipH, 8);
  const contentH = flow.h;
  const maxScroll = Math.max(0, contentH - innerH);
  ui.scrollY = Math.max(0, Math.min(ui.scrollY, maxScroll));

  ctx.save();
  ctx.beginPath();
  ctx.rect(innerX - 2, innerY, innerW + 8, innerH);
  ctx.clip();
  if (words.length === 0) {
    ctx.fillStyle = C.ink3;
    ctx.font = `13px ${TYPE.sans}`;
    ctx.fillText("本局暂无联句", innerX + 2, innerY + 14);
  }
  for (let i = 0; i < flow.chips.length; i++) {
    const c = flow.chips[i]!;
    const x = innerX + c.x;
    const y = innerY + c.y - ui.scrollY;
    if (y + chipH < innerY - 2 || y > innerY + innerH + 2) continue;
    const last = i === flow.chips.length - 1;
    fillRound(ctx, x, y, c.w, chipH, chipH / 2, last ? C.sealSoft : C.cardSoft);
    ctx.strokeStyle = last ? "rgba(184,58,36,0.3)" : C.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x + chipH / 2, y + 0.5);
    ctx.arcTo(x + c.w - 0.5, y + 0.5, x + c.w - 0.5, y + chipH - 0.5, chipH / 2 - 0.5);
    ctx.arcTo(x + c.w - 0.5, y + chipH - 0.5, x + 0.5, y + chipH - 0.5, chipH / 2 - 0.5);
    ctx.arcTo(x + 0.5, y + chipH - 0.5, x + 0.5, y + 0.5, chipH / 2 - 0.5);
    ctx.arcTo(x + 0.5, y + 0.5, x + c.w - 0.5, y + 0.5, chipH / 2 - 0.5);
    ctx.closePath();
    ctx.stroke();
    ctx.fillStyle = last ? C.seal : C.ink;
    ctx.font = `15px ${TYPE.serif}`;
    ctx.textAlign = "left";
    ctx.fillText(c.word, x + 10, y + chipH / 2 + 1);
    const next = flow.chips[i + 1];
    if (next && next.y === c.y) {
      ctx.fillStyle = "rgba(184,58,36,0.45)";
      ctx.font = `13px ${TYPE.sans}`;
      ctx.textAlign = "center";
      ctx.fillText("›", x + c.w + 7, y + chipH / 2);
    }
  }
  ctx.restore();

  if (maxScroll > 0) {
    const trackX = card.x + card.w - 9;
    const trackY = innerY;
    const trackH = innerH;
    const thumbH = Math.max(18, (trackH * innerH) / contentH);
    const thumbY = trackY + (trackH - thumbH) * (ui.scrollY / maxScroll);
    fillRound(ctx, trackX, trackY, 3, trackH, 1.5, "rgba(92,62,32,0.08)");
    fillRound(ctx, trackX, thumbY, 3, thumbH, 1.5, "rgba(92,62,32,0.3)");
    if (ui.scrollY <= 0) {
      ctx.fillStyle = C.ink3;
      ctx.font = `12px ${TYPE.sans}`;
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillText("上滑查看更多", card.x + card.w - 16, card.y + 20);
    }
  }
}
